import cx from 'classnames'
import { TOC_CONTAINER_ID } from 'common/Prose/components/TOC/PortalTOC'
import SocialFloat from 'components/SocialFloat/SocialFloat'
import { isEqual } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useSelector } from 'react-redux'
import { EDITOR_CONTAINER_ID } from 'screens/ProseEdit/Constants'
import { useDocStore } from 'stores/DocStore'
import { DocMode } from 'stores/DocStore.types'
import { ArticleFlat, Layer, Profile, RootState, UserRole } from 'types'
import { isAlmanacArticle } from 'utils'
import { canRenderCoverPhoto } from 'utils/articleUtils'
import Styles from '../../ProseEdit.module.scss'
import { CoverPhoto } from '../CoverPhoto/CoverPhoto'
import { NerudaEditor } from '../NerudaEditor/NerudaEditor'
import { ErrorBoundary } from '@sentry/react'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useHistory } from 'react-router-dom'
import { useArticleViewLink } from 'hooks/useArticleViewLink'
import { useAbility } from '@casl/react'
import { AbilityContext } from 'components/Can'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

interface Props {
  article: ArticleFlat
  layer: Layer
  creator?: Profile
  leftRailOpen: boolean
}

const ArticleReader = observer(({ article, layer, leftRailOpen, creator }: Props) => {
  const workspace = useCurrentWorkspace()
  const docStore = useDocStore()
  const docModeIsEditor = docStore.docMode === DocMode.Editor
  const metaData = useSelector((state: RootState) => state.articleMeta[article.id], isEqual)
  const history = useHistory()
  const editLinkProps = useArticleViewLink()
  const ability = useAbility(AbilityContext)
  const authedArticle = {
    role: workspace?.role || UserRole.UNAUTHED,
    permissions: metaData?.almanac_file.permissions,
    __caslSubjectType__: 'Article',
  }

  const handleDoubleClick = () => {
    if (ability.can('edit', authedArticle)) {
      history.push({ ...editLinkProps.to })
    }
    return false
  }

  return (
    <>
      {metaData && canRenderCoverPhoto(metaData) && <CoverPhoto articleMeta={metaData} />}
      <div className={Styles.columnContainer}>
        <div className={cx(Styles.leftColumn)}>
          <div className={Styles.nerudaTocContainer} id={TOC_CONTAINER_ID} />
        </div>
        <div className={Styles.midColumn}>
          <div className={Styles.contentContainer}>
            <div id={EDITOR_CONTAINER_ID} className={Styles.editorContainer}>
              <NerudaEditor
                layer={layer}
                mode="read"
                article={article}
                readOnly
                rightRailProps={{
                  show: false,
                  showSnippetsRail: false,
                  docModeIsEditor,
                  leftRailOpen,
                }}
                editorProps={{
                  handleDoubleClick,
                }}
              />
            </div>
          </div>
        </div>
        <div className={Styles.rightColumn}>
          {isAlmanacArticle(article) && (
            <div className={Styles.stickySocialContainer}>
              <div className={Styles.stickySocial}>
                <SocialFloat
                  articleId={article.id}
                  title={article.title}
                  articleLink={article.article_link}
                  twitter={creator && creator.twitter}
                  showRailToggle
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
})

const WithError = (props: Props) => (
  <ErrorBoundary
    onError={() =>
      showErrorMessageToast(
        'Failed to display document. Please try to refresh the page. If this error persists, please contact support.',
      )
    }
  >
    <ArticleReader {...props} />
  </ErrorBoundary>
)

export { WithError as ArticleReader }
